import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Input,
  Spinner,
  Table,
  Td,
  Th,
} from '@/components';
import { AudioPreviewButton } from '@/components/admin/AudioPreviewButton';
import { ResourceCardColorPicker } from '@/components/admin/ResourceCardColorPicker';
import {
  FLASHCARD_CARD_COLORS,
  type AdminFlashcardSet,
  type FlashcardExtractedCard,
  type FlashcardIngestJob,
  type FlashcardMatchResponse,
  type FlashcardMediaUpload,
} from '@/lib/admin-types';
import { apiJson, getApiErrorMessage } from '@/lib/api';
import { expandAudioUploads } from '@/lib/flashcard-audio-zip';
import {
  resourceCardColorPayload,
  type ResourceCardColorOption,
} from '@/lib/resource-card-colors';

type DraftCard = {
  localId: string;
  id?: number;
  sort_order: number;
  title: string;
  translation_en: string;
  card_color: string;
  persistency_key?: string;
  image_cdn_key: string | null;
  image_url: string | null;
  sound_adult_cdn_key: string | null;
  sound_adult_url: string | null;
  sound_children_cdn_key: string | null;
  sound_children_url: string | null;
  is_cover: boolean;
  needs_translation_smaller: boolean;
};

type UploadedMedia = {
  original_filename: string;
  cdn_key: string;
  url: string;
  voice?: string | null;
};

function newLocalId(): string {
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

function screenshotLabel(cdnKey: string | undefined): string {
  if (!cdnKey) return 'screenshot';
  const name = cdnKey.split('/').pop() || cdnKey;
  const underscore = name.indexOf('_');
  if (underscore >= 0 && underscore < name.length - 1) {
    return name.slice(underscore + 1);
  }
  return name;
}

function formatExtractFailures(
  failed: NonNullable<FlashcardIngestJob['result']>['failed'] | undefined,
  opts?: { extractedCount?: number; fallback?: string }
): string {
  const items = failed ?? [];
  if (!items.length) {
    return opts?.fallback || 'Screenshot extraction failed';
  }

  const lines = items.map((f) => {
    const label = f.original_filename || screenshotLabel(f.cdn_key);
    const error = (f.error || 'unknown error').trim() || 'unknown error';
    return `• ${label}: ${error}`;
  });

  const uniqueErrors = [
    ...new Set(
      items.map((f) => (f.error || 'unknown error').trim() || 'unknown error')
    ),
  ].sort();
  const extractedCount = opts?.extractedCount ?? 0;

  if (extractedCount > 0) {
    return (
      `Extracted ${extractedCount} card(s); ${items.length} screenshot(s) failed — review before matching media\n` +
      lines.join('\n')
    );
  }

  if (uniqueErrors.length === 1) {
    const names = items
      .map((f) => f.original_filename || screenshotLabel(f.cdn_key))
      .join(', ');
    return `${items.length} screenshot(s) failed extraction: ${uniqueErrors[0]}\nAffected: ${names}`;
  }

  return `${items.length} screenshot(s) failed extraction\n${lines.join('\n')}`;
}

function draftFromExtracted(cards: FlashcardExtractedCard[]): DraftCard[] {
  return cards.map((c, idx) => ({
    localId: newLocalId(),
    sort_order: idx,
    title: c.title,
    translation_en: c.translations?.en || '',
    card_color: c.card_color || 'LIGHT_RED',
    image_cdn_key: null,
    image_url: null,
    sound_adult_cdn_key: null,
    sound_adult_url: null,
    sound_children_cdn_key: null,
    sound_children_url: null,
    is_cover: c.is_cover,
    needs_translation_smaller: c.needs_translation_smaller,
  }));
}

function draftFromSet(set: AdminFlashcardSet): DraftCard[] {
  return set.cards.map((c, idx) => ({
    localId: `db-${c.id}`,
    id: c.id,
    sort_order: c.sort_order ?? idx,
    title: c.title,
    translation_en: c.translations?.en || '',
    card_color: c.card_color,
    persistency_key: c.persistency_key,
    image_cdn_key: c.image_cdn_key,
    image_url: c.image_url,
    sound_adult_cdn_key: c.sound_adult_cdn_key,
    sound_adult_url: c.sound_url,
    sound_children_cdn_key: c.sound_children_cdn_key,
    sound_children_url: c.sound_children_url,
    is_cover: c.is_cover,
    needs_translation_smaller: c.needs_translation_smaller,
  }));
}

function validateDraft(
  cards: DraftCard[],
  audioVoices: string,
  hasCoverCard: boolean
): string[] {
  const errors: string[] = [];
  if (cards.length === 0) {
    errors.push('Add at least one card');
    return errors;
  }
  // Homonyms are valid (same Hmong title, different English meanings).
  // Only identical title+en pairs are treated as duplicates.
  const titleEnPairs = new Set<string>();
  let covers = 0;
  const singleVoice = audioVoices === 'single';
  for (const card of cards) {
    const label = card.title || 'Untitled card';
    if (card.is_cover) {
      covers += 1;
      continue;
    }
    if (!card.title.trim()) errors.push(`${label}: title is required`);
    if (!card.translation_en.trim()) errors.push(`${label}: English translation is required`);
    const t = card.title.trim().toLowerCase();
    const en = card.translation_en.trim().toLowerCase();
    if (t && en) {
      const pair = `${t}\0${en}`;
      if (titleEnPairs.has(pair)) {
        errors.push(`Duplicate card: ${card.title} / ${card.translation_en.trim()}`);
      }
      titleEnPairs.add(pair);
    }
    if (!card.image_cdn_key) errors.push(`${label}: image is required`);
    if (singleVoice) {
      if (!card.sound_adult_cdn_key && !card.sound_children_cdn_key) {
        errors.push(`${label}: audio is required`);
      }
    } else {
      if (!card.sound_adult_cdn_key) errors.push(`${label}: adult audio is required`);
      if (!card.sound_children_cdn_key) errors.push(`${label}: child audio is required`);
    }
  }
  if (hasCoverCard && covers === 0) errors.push('Mark a cover card or turn off has_cover_card');
  if (covers > 1) errors.push('Only one cover card is allowed');
  return errors;
}

async function uploadFile(
  setId: number,
  kind: 'screenshots' | 'images' | 'audio',
  file: File,
  extra?: { voice?: string; convert_sync?: boolean; original_filename?: string }
): Promise<FlashcardMediaUpload> {
  const form = new FormData();
  form.append('file', file);
  if (kind === 'audio') {
    if (extra?.voice) form.append('voice', extra.voice);
    if (extra?.original_filename) form.append('original_filename', extra.original_filename);
    form.append('convert_sync', extra?.convert_sync ? 'true' : 'false');
  }
  return apiJson<FlashcardMediaUpload>(
    `/v1/admin/flashcard-sets/${setId}/${kind}/upload`,
    { method: 'POST', body: form }
  );
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function FlashcardSetDetailPage() {
  const { id } = Route.useParams();
  const setId = Number(id);
  const qc = useQueryClient();

  const { data: set, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'flashcard-sets', setId],
    queryFn: () => apiJson<AdminFlashcardSet>(`/v1/admin/flashcard-sets/${setId}`),
  });

  const [draftCards, setDraftCards] = useState<DraftCard[] | null>(null);
  const [screenshots, setScreenshots] = useState<UploadedMedia[]>([]);
  const [images, setImages] = useState<UploadedMedia[]>([]);
  const [audioFiles, setAudioFiles] = useState<UploadedMedia[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [name, setName] = useState<string | null>(null);
  const [hasCoverCard, setHasCoverCard] = useState<boolean | null>(null);
  const [requiresSubscriptionDraft, setRequiresSubscriptionDraft] = useState<boolean | null>(
    null
  );
  const [audioVoicesDraft, setAudioVoicesDraft] = useState<'dual' | 'single' | null>(null);
  const [statusDraft, setStatusDraft] = useState<string | null>(null);
  const [backgroundColorDraft, setBackgroundColorDraft] = useState<string | null>(
    null
  );
  const [textColorDraft, setTextColorDraft] = useState<string | null>(null);
  const [badgeBackgroundDraft, setBadgeBackgroundDraft] = useState<string | null>(
    null
  );

  const cards = draftCards ?? (set ? draftFromSet(set) : []);
  const coverEnabled = hasCoverCard ?? set?.has_cover_card ?? false;
  const requiresSubscription =
    requiresSubscriptionDraft ?? set?.requires_subscription ?? false;
  const displayName = name ?? set?.name ?? '';
  const audioVoices = audioVoicesDraft ?? (set?.audio_voices === 'single' ? 'single' : 'dual');
  const singleVoice = audioVoices === 'single';
  const status = statusDraft ?? set?.status ?? 'draft';
  const backgroundColor = backgroundColorDraft ?? set?.background_color ?? '';
  const textColor = textColorDraft ?? set?.text_color ?? '';
  const badgeBackground = badgeBackgroundDraft ?? set?.badge_background ?? '';

  function onPickCardColor(option: ResourceCardColorOption) {
    const payload = resourceCardColorPayload(option);
    setBackgroundColorDraft(payload.background_color);
    setTextColorDraft(payload.text_color);
    setBadgeBackgroundDraft(payload.badge_background);
  }

  const validationErrors = useMemo(
    () => (set ? validateDraft(cards, audioVoices, coverEnabled) : []),
    [cards, set, audioVoices, coverEnabled]
  );

  const audioUrlByCdnKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of audioFiles) {
      if (a.cdn_key && a.url) map.set(a.cdn_key, a.url);
    }
    return map;
  }, [audioFiles]);

  function resolveSoundUrl(
    storedUrl: string | null,
    cdnKey: string | null
  ): string | null {
    return storedUrl || (cdnKey ? audioUrlByCdnKey.get(cdnKey) ?? null : null);
  }

  const saveCards = useMutation({
    mutationFn: async () => {
      if (!set) return;
      const payload = {
        cards: cards.map((c, idx) => ({
          id: c.id,
          sort_order: idx,
          title: c.title,
          translations: { en: c.translation_en },
          card_color: c.card_color,
          persistency_key: c.persistency_key,
          image_cdn_key: c.image_cdn_key,
          sound_adult_cdn_key: c.sound_adult_cdn_key,
          sound_children_cdn_key: singleVoice
            ? c.sound_adult_cdn_key || c.sound_children_cdn_key
            : c.sound_children_cdn_key,
          is_cover: c.is_cover,
          needs_translation_smaller: c.needs_translation_smaller,
        })),
      };
      return apiJson<AdminFlashcardSet>(`/v1/admin/flashcard-sets/${setId}/cards`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: (updated) => {
      if (!updated) return;
      setDraftCards(draftFromSet(updated));
      void qc.setQueryData(['admin', 'flashcard-sets', setId], updated);
      setMessage('Draft cards saved');
    },
  });

  const saveMeta = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<AdminFlashcardSet>(`/v1/admin/flashcard-sets/${setId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      void qc.setQueryData(['admin', 'flashcard-sets', setId], updated);
      setStatusDraft(updated.status);
      setHasCoverCard(updated.has_cover_card);
      setRequiresSubscriptionDraft(updated.requires_subscription);
      setName(updated.name);
      setAudioVoicesDraft(null);
      setBackgroundColorDraft(null);
      setTextColorDraft(null);
      setBadgeBackgroundDraft(null);
      setMessage(updated.status === 'live' ? 'Set is live' : 'Settings saved');
      void qc.invalidateQueries({ queryKey: ['admin', 'flashcard-sets'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError || !set) {
    return (
      <p className="text-sm text-red-600">
        {getApiErrorMessage(error)}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  const canPublish = validationErrors.length === 0 && cards.length > 0;

  async function onUploadScreenshots(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Uploading screenshots…');
    setMessage(null);
    try {
      const uploaded: UploadedMedia[] = [];
      for (const file of Array.from(fileList)) {
        const res = await uploadFile(setId, 'screenshots', file);
        uploaded.push({
          original_filename: res.original_filename,
          cdn_key: res.cdn_key,
          url: res.url,
        });
      }
      setScreenshots((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} screenshot(s)`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onExtract() {
    if (screenshots.length === 0) {
      setMessage('Upload screenshots first');
      return;
    }
    setBusy('Extracting cards with LLM…');
    setMessage(null);
    try {
      // Run as a background ingest job so App Platform's ~60s gateway cannot
      // return a raw HTML 502 while OpenAI vision is still working.
      const job = await apiJson<FlashcardIngestJob>(
        `/v1/admin/flashcard-sets/${setId}/extract`,
        {
          method: 'POST',
          body: JSON.stringify({
            screenshot_cdn_keys: screenshots.map((s) => s.cdn_key),
          }),
        }
      );
      let current = job;
      for (let i = 0; i < 300; i++) {
        if (['completed', 'partial_failed', 'failed'].includes(current.status)) break;
        setBusy(`Extracting cards with LLM… (${i + 1}s)`);
        await sleep(1000);
        current = await apiJson<FlashcardIngestJob>(
          `/v1/admin/flashcard-sets/${setId}/jobs/${current.job_id}`
        );
      }
      if (!['completed', 'partial_failed', 'failed'].includes(current.status)) {
        throw new Error(
          'Extract is still running after 5 minutes. Refresh and check again, or retry with fewer screenshots.'
        );
      }
      if (current.status === 'failed') {
        throw new Error(
          formatExtractFailures(current.result?.failed, {
            fallback: current.error_message || 'Extract job failed',
          })
        );
      }
      const cards = (current.result?.cards || []) as FlashcardExtractedCard[];
      if (!cards.length) {
        throw new Error(
          formatExtractFailures(current.result?.failed, {
            fallback:
              current.error_message ||
              'No cards extracted — try clearer screenshots',
          })
        );
      }
      setDraftCards(draftFromExtracted(cards));
      const failed = current.result?.failed ?? [];
      setMessage(
        failed.length
          ? formatExtractFailures(failed, { extractedCount: cards.length })
          : `Extracted ${cards.length} card(s) — review before matching media`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onUploadImages(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Uploading images…');
    try {
      const uploaded: UploadedMedia[] = [];
      for (const file of Array.from(fileList)) {
        const res = await uploadFile(setId, 'images', file);
        uploaded.push({
          original_filename: res.original_filename,
          cdn_key: res.cdn_key,
          url: res.url,
        });
      }
      setImages((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} image(s)`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onUploadAudio(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Preparing audio…');
    setMessage(null);
    try {
      const expanded = await expandAudioUploads(fileList);
      const adultCount = expanded.filter((e) => e.voice === 'adult').length;
      const childCount = expanded.filter((e) => e.voice === 'children').length;
      const unknownCount = expanded.length - adultCount - childCount;

      setBusy(`Uploading + converting audio… (0/${expanded.length})`);
      const rawUploads: FlashcardMediaUpload[] = [];
      for (let i = 0; i < expanded.length; i++) {
        const item = expanded[i]!;
        setBusy(`Uploading + converting audio… (${i + 1}/${expanded.length})`);
        // Convert on the API (sync) so we don't depend on Celery workers having
        // a system ffmpeg package. Fall back to the async job if a raw upload
        // is returned for any reason.
        const res = await uploadFile(setId, 'audio', item.file, {
          convert_sync: true,
          voice: item.voice ?? undefined,
          original_filename: item.relativePath,
        });
        // Prefer the zip-relative path so match can see kid/ vs kristina/.
        rawUploads.push({
          ...res,
          original_filename: res.original_filename || item.relativePath,
          voice: res.voice ?? item.voice,
        });
      }

      // Sync convert is preferred; fall back to job polling for any raw leftovers.
      const converted: UploadedMedia[] = [];
      const stillRaw: FlashcardMediaUpload[] = [];
      for (const raw of rawUploads) {
        if (raw.cdn_key.includes('/audio/') && raw.content_type === 'audio/mpeg') {
          converted.push({
            original_filename: raw.original_filename,
            cdn_key: raw.cdn_key,
            url: raw.url,
            voice: raw.voice,
          });
        } else {
          stillRaw.push(raw);
        }
      }

      if (stillRaw.length) {
        const job = await apiJson<FlashcardIngestJob>(
          `/v1/admin/flashcard-sets/${setId}/audio/convert`,
          {
            method: 'POST',
            body: JSON.stringify({
              files: stillRaw.map((r) => ({
                original_filename: r.original_filename,
                raw_cdn_key: r.cdn_key,
                voice: r.voice,
              })),
            }),
          }
        );
        let current = job;
        for (let i = 0; i < 180; i++) {
          if (['completed', 'partial_failed', 'failed'].includes(current.status)) break;
          await sleep(1000);
          current = await apiJson<FlashcardIngestJob>(
            `/v1/admin/flashcard-sets/${setId}/jobs/${current.job_id}`
          );
        }
        for (const item of current.result?.converted || []) {
          converted.push({
            original_filename: item.original_filename,
            cdn_key: item.cdn_key,
            url: item.url || '',
            voice: item.voice,
          });
        }
        if (current.result?.failed?.length) {
          setMessage(
            `Converted ${converted.length}; failed: ${current.result.failed
              .map((f) => f.original_filename)
              .join(', ')}`
          );
        } else {
          const voiceHint = singleVoice
              ? ''
              : ` (adult: ${adultCount}, child: ${childCount}${
                  unknownCount ? `, unknown: ${unknownCount}` : ''
                })`;
          setMessage(`Converted ${converted.length} audio file(s)${voiceHint}`);
        }
      } else {
        setMessage(`Ready ${converted.length} audio file(s)`);
      }
      setAudioFiles((prev) => [...prev, ...converted]);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onMatchMedia() {
    if (cards.length === 0) {
      setMessage('Extract or add cards first');
      return;
    }
    setBusy('Matching media with LLM…');
    try {
      const res = await apiJson<FlashcardMatchResponse>(
        `/v1/admin/flashcard-sets/${setId}/match-media`,
        {
          method: 'POST',
          body: JSON.stringify({
            cards: cards.map((c, index) => ({
              index,
              title: c.title,
              translations: { en: c.translation_en },
            })),
            image_filenames: images.map((i) => i.original_filename),
            audio_filenames: audioFiles.map((a) => a.original_filename),
          }),
        }
      );
      const imageByName = new Map(images.map((i) => [i.original_filename, i]));
      const audioByName = new Map(audioFiles.map((a) => [a.original_filename, a]));
      setDraftCards((prev) => {
        const base = prev ?? cards;
        return base.map((card, idx) => {
          const assignment = res.assignments.find((a) => a.card_index === idx);
          if (!assignment) return card;
          const img = assignment.image_filename
            ? imageByName.get(assignment.image_filename)
            : undefined;
          const adult = assignment.sound_adult_filename
            ? audioByName.get(assignment.sound_adult_filename)
            : undefined;
          const child = assignment.sound_children_filename
            ? audioByName.get(assignment.sound_children_filename)
            : undefined;
          return {
            ...card,
            image_cdn_key: img?.cdn_key ?? card.image_cdn_key,
            image_url: img?.url ?? card.image_url,
            sound_adult_cdn_key: adult?.cdn_key ?? card.sound_adult_cdn_key,
            sound_adult_url: adult?.url ?? card.sound_adult_url,
            sound_children_cdn_key: child?.cdn_key ?? card.sound_children_cdn_key,
            sound_children_url: child?.url ?? card.sound_children_url,
          };
        });
      });
      setMessage(
        `Matched media. Unmatched images: ${res.unmatched_images.length}, audio: ${res.unmatched_audio.length}`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  function updateCard(localId: string, patch: Partial<DraftCard>) {
    setDraftCards((prev) => {
      const base = prev ?? cards;
      return base.map((c) => (c.localId === localId ? { ...c, ...patch } : c));
    });
  }

  function removeCard(localId: string) {
    setDraftCards((prev) => {
      const base = prev ?? cards;
      return base.filter((c) => c.localId !== localId);
    });
  }

  async function ensureConvertedAudio(
    raw: FlashcardMediaUpload,
    voice: 'adult' | 'children'
  ): Promise<UploadedMedia> {
    if (raw.cdn_key.includes('/audio/') && raw.content_type === 'audio/mpeg') {
      return {
        original_filename: raw.original_filename,
        cdn_key: raw.cdn_key,
        url: raw.url,
        voice: raw.voice ?? voice,
      };
    }

    const job = await apiJson<FlashcardIngestJob>(
      `/v1/admin/flashcard-sets/${setId}/audio/convert`,
      {
        method: 'POST',
        body: JSON.stringify({
          files: [
            {
              original_filename: raw.original_filename,
              raw_cdn_key: raw.cdn_key,
              voice: raw.voice ?? voice,
            },
          ],
        }),
      }
    );
    let current = job;
    for (let i = 0; i < 180; i++) {
      if (['completed', 'partial_failed', 'failed'].includes(current.status)) break;
      await sleep(1000);
      current = await apiJson<FlashcardIngestJob>(
        `/v1/admin/flashcard-sets/${setId}/jobs/${current.job_id}`
      );
    }
    const converted = current.result?.converted?.[0];
    if (!converted) {
      const failedName = current.result?.failed?.[0]?.original_filename;
      throw new Error(
        failedName
          ? `Audio conversion failed for ${failedName}`
          : current.error_message || 'Audio conversion failed'
      );
    }
    return {
      original_filename: converted.original_filename,
      cdn_key: converted.cdn_key,
      url: converted.url || '',
      voice: converted.voice ?? voice,
    };
  }

  async function onReplaceCardImage(localId: string, fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setBusy('Replacing image…');
    setMessage(null);
    try {
      const res = await uploadFile(setId, 'images', file);
      const media: UploadedMedia = {
        original_filename: res.original_filename,
        cdn_key: res.cdn_key,
        url: res.url,
      };
      setImages((prev) => [...prev, media]);
      updateCard(localId, {
        image_cdn_key: media.cdn_key,
        image_url: media.url,
      });
      setMessage('Image updated — save draft cards to persist');
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onReplaceCardAudio(
    localId: string,
    voice: 'adult' | 'children',
    fileList: FileList | null
  ) {
    const file = fileList?.[0];
    if (!file) return;
    const voiceLabel = voice === 'adult' ? (singleVoice ? 'audio' : 'adult audio') : 'child audio';
    setBusy(`Replacing ${voiceLabel}…`);
    setMessage(null);
    try {
      const raw = await uploadFile(setId, 'audio', file, {
        convert_sync: true,
        voice,
      });
      const media = await ensureConvertedAudio(
        { ...raw, voice: raw.voice ?? voice },
        voice
      );
      setAudioFiles((prev) => [...prev, media]);
      if (voice === 'adult') {
        updateCard(localId, {
          sound_adult_cdn_key: media.cdn_key,
          sound_adult_url: media.url,
        });
      } else {
        updateCard(localId, {
          sound_children_cdn_key: media.cdn_key,
          sound_children_url: media.url,
        });
      }
      setMessage(
        `${voice === 'adult' ? (singleVoice ? 'Audio' : 'Adult audio') : 'Child audio'} updated — save draft cards to persist`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/admin/flashcard-sets" className="text-sm text-primary hover:underline">
            ← All flashcard sets
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{set.name}</h1>
            <Badge variant={status === 'live' ? 'success' : 'neutral'}>
              {status === 'live' ? 'Live' : 'Draft'}
            </Badge>
            <Badge variant="neutral">{set.mode}</Badge>
            <Badge variant="neutral">
              {singleVoice ? 'single voice' : 'dual voice'}
            </Badge>
            {requiresSubscription ? <Badge variant="warning">Premium</Badge> : null}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {set.slug} · v{set.content_version}
          </p>
        </div>
      </div>

      {busy ? <p className="text-sm text-amber-700">{busy}</p> : null}
      {message ? (
        <p className="whitespace-pre-wrap text-sm text-gray-700">{message}</p>
      ) : null}

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">Set settings</h2>
        <div className="flex flex-wrap gap-3">
          <Input
            value={displayName}
            onChange={(e) => setName(e.target.value)}
            className="max-w-xs"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={coverEnabled}
              onChange={(e) => setHasCoverCard(e.target.checked)}
            />
            Has cover card
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresSubscription}
              onChange={(e) => setRequiresSubscriptionDraft(e.target.checked)}
            />
            Subscribers only
          </label>
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={audioVoices}
            onChange={(e) => setAudioVoicesDraft(e.target.value as 'dual' | 'single')}
          >
            <option value="dual">Dual voice (adult + child)</option>
            <option value="single">Single voice</option>
          </select>
          <Button
            disabled={saveMeta.isPending}
            onClick={() =>
              saveMeta.mutate({
                name: displayName.trim(),
                has_cover_card: coverEnabled,
                audio_voices: audioVoices,
                requires_subscription: requiresSubscription,
                background_color: backgroundColor,
                text_color: textColor,
                badge_background: badgeBackground,
              })
            }
          >
            Save settings
          </Button>
        </div>
        <ResourceCardColorPicker
          value={backgroundColor}
          onChange={onPickCardColor}
          disabled={saveMeta.isPending}
        />
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">1. Screenshots → extract cards</h2>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => void onUploadScreenshots(e.target.files)}
        />
        <p className="text-xs text-gray-500">{screenshots.length} screenshot(s) uploaded</p>
        <Button disabled={!!busy || screenshots.length === 0} onClick={() => void onExtract()}>
          Extract with LLM
        </Button>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">2. Upload media</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-sm text-gray-700">Card images</p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => void onUploadImages(e.target.files)}
            />
            <p className="mt-1 text-xs text-gray-500">{images.length} image(s)</p>
          </div>
          <div>
            <p className="mb-1 text-sm text-gray-700">Audio (files or zip)</p>
            <input
              type="file"
              accept="audio/*,.wav,.m4a,.amr,.mp3,.zip,application/zip"
              multiple
              onChange={(e) => void onUploadAudio(e.target.files)}
            />
            <p className="mt-1 text-xs text-gray-500">
              {audioFiles.length} audio file(s)
              {audioFiles.filter((a) => a.voice === 'adult').length ||
              audioFiles.filter((a) => a.voice === 'children').length
                ? ` · adult ${audioFiles.filter((a) => a.voice === 'adult').length}, child ${audioFiles.filter((a) => a.voice === 'children').length}`
                : ''}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Zip layout for dual voice: <code>kid/</code> (or <code>children/</code>) +{' '}
              <code>kristina/</code> (adult). Single voice: any single folder of audio is fine.
            </p>
          </div>
        </div>
        <Button
          disabled={!!busy || (images.length === 0 && audioFiles.length === 0)}
          onClick={() => void onMatchMedia()}
        >
          Match media with LLM
        </Button>
      </Card>

      <Card className="space-y-3 overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-gray-900">3. Review cards</h2>
          <Button
            disabled={saveCards.isPending || cards.length === 0}
            onClick={() => {
              setClientErrors(validationErrors);
              saveCards.mutate();
            }}
          >
            {saveCards.isPending ? 'Saving…' : 'Save draft cards'}
          </Button>
        </div>
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Title</Th>
              <Th>English</Th>
              <Th>Color</Th>
              <Th>Image</Th>
              <Th>Adult</Th>
              {!singleVoice ? <Th>Child</Th> : null}
              <Th>Cover</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {cards.map((card, idx) => (
              <tr key={card.localId}>
                <Td>{idx + 1}</Td>
                <Td>
                  <Input
                    value={card.title}
                    onChange={(e) => updateCard(card.localId, { title: e.target.value })}
                  />
                </Td>
                <Td>
                  <Input
                    value={card.translation_en}
                    onChange={(e) =>
                      updateCard(card.localId, { translation_en: e.target.value })
                    }
                  />
                </Td>
                <Td>
                  <select
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                    value={card.card_color}
                    onChange={(e) => updateCard(card.localId, { card_color: e.target.value })}
                  >
                    {FLASHCARD_CARD_COLORS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Td>
                <Td>
                  <div className="flex flex-col items-start gap-1">
                    {card.image_url ? (
                      <img src={card.image_url} alt="" className="h-10 w-10 object-contain" />
                    ) : (
                      <span className="text-xs text-red-600">missing</span>
                    )}
                    <label
                      className={`text-xs text-primary hover:underline ${
                        busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                      }`}
                    >
                      {card.image_url ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!!busy}
                        onChange={(e) => {
                          void onReplaceCardImage(card.localId, e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </Td>
                <Td>
                  <div className="flex flex-col items-start gap-1">
                    <AudioPreviewButton
                      url={resolveSoundUrl(card.sound_adult_url, card.sound_adult_cdn_key)}
                      cdnKey={card.sound_adult_cdn_key}
                      label={`${card.title || 'card'} ${singleVoice ? 'audio' : 'adult audio'}`}
                    />
                    <label
                      className={`text-xs text-primary hover:underline ${
                        busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                      }`}
                    >
                      {card.sound_adult_cdn_key ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        accept="audio/*,.wav,.m4a,.amr,.mp3"
                        className="hidden"
                        disabled={!!busy}
                        onChange={(e) => {
                          void onReplaceCardAudio(card.localId, 'adult', e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </Td>
                {singleVoice ? null : (
                  <Td>
                    <div className="flex flex-col items-start gap-1">
                      <AudioPreviewButton
                        url={resolveSoundUrl(
                          card.sound_children_url,
                          card.sound_children_cdn_key
                        )}
                        cdnKey={card.sound_children_cdn_key}
                        label={`${card.title || 'card'} child audio`}
                      />
                      <label
                        className={`text-xs text-primary hover:underline ${
                          busy ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                        }`}
                      >
                        {card.sound_children_cdn_key ? 'Replace' : 'Upload'}
                        <input
                          type="file"
                          accept="audio/*,.wav,.m4a,.amr,.mp3"
                          className="hidden"
                          disabled={!!busy}
                          onChange={(e) => {
                            void onReplaceCardAudio(card.localId, 'children', e.target.files);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </Td>
                )}
                <Td>
                  <input
                    type="checkbox"
                    checked={card.is_cover}
                    onChange={(e) => updateCard(card.localId, { is_cover: e.target.checked })}
                  />
                </Td>
                <Td>
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-red-600"
                    onClick={() => removeCard(card.localId)}
                  >
                    Remove
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">4. Publish</h2>
        {(clientErrors.length > 0 || validationErrors.length > 0) && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-red-600">
            {(clientErrors.length ? clientErrors : validationErrors).map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={!canPublish || saveMeta.isPending || saveCards.isPending}
            onClick={async () => {
              const errs = validateDraft(cards, audioVoices, coverEnabled);
              setClientErrors(errs);
              if (errs.length) return;
              await saveCards.mutateAsync();
              saveMeta.mutate({ status: 'live', has_cover_card: coverEnabled });
            }}
          >
            Publish live
          </Button>
          {status === 'live' ? (
            <Button
              disabled={saveMeta.isPending}
              onClick={() => saveMeta.mutate({ status: 'draft' })}
            >
              Unpublish (draft)
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-gray-500">
          Publishing is blocked until every non-cover card has title, English, image, and required
          audio ({singleVoice ? 'one voice' : 'adult + child'}).
        </p>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/flashcard-sets/$id')({
  component: FlashcardSetDetailPage,
});
